import { Plugin } from 'chart.js';
import { ChartColors } from './constants';
import { DataSet, FloatingLabelConfig, LabelPosition } from './types';

const DEFAULT_FONT_SIZE = '14px';
const DEFAULT_FONT = 'Lato';

export const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

export const drawTopLabels = (
  ctx: CanvasRenderingContext2D,
  label: FloatingLabelConfig,
  xAnchor: number,
  yTopAnchor: number,
  barWidth: number,
  boldFont: string,
  font: string
) => {
  const lineHeight = 19;
  [...label.topLabel].reverse().forEach(({ value, textColor, bold = false }, labelIdx) => {
    ctx.font = bold ? boldFont : font;
    ctx.fillStyle = textColor;
    const lines = wrapText(ctx, value, barWidth);
    lines.forEach((line, lineIdx) => {
      ctx.fillText(line, xAnchor, yTopAnchor - lineHeight * (labelIdx + lineIdx));
    });
  });
};

export const drawBottomLabels = (
  ctx: CanvasRenderingContext2D,
  label: FloatingLabelConfig,
  xAnchor: number,
  yBottomAnchor: number,
  barWidth: number,
  boldFont: string,
  font: string
) => {
  let totalOffset = 0;
  const lineHeight = 19;
  label.bottomLabel?.forEach(({ value, textColor, bold = false }) => {
    ctx.font = bold ? boldFont : font;
    ctx.fillStyle = textColor;
    const lines = wrapText(ctx, value, barWidth);
    lines.forEach((line, lineIdx) => {
      ctx.fillText(line, xAnchor, yBottomAnchor + lineHeight * (totalOffset + lineIdx));
    });
    totalOffset += lines.length;
  });
};

export function floatingBarLabelsPlugin(labels: FloatingLabelConfig[]): Plugin {
  return {
    id: 'floatingBarLabels',
    afterDatasetDraw(chart) {
      const {
        ctx,
        data,
        scales: { x, y },
      } = chart;
      const font = `${DEFAULT_FONT_SIZE} ${DEFAULT_FONT}`;
      const boldFont = `bold ${font}`;
      ctx.textAlign = 'left';

      const segmentWidth = x.getPixelForValue(1) - x.getPixelForValue(0);
      const barPercentage =
        'barPercentage' in chart.config.data.datasets[0] ? chart.config.data.datasets[0].barPercentage : 1;
      const barWidth = segmentWidth * barPercentage;

      labels.forEach((label, idx) => {
        if (!label) return;

        const leftPadding = x.getPixelForValue(idx);
        const xAnchor = leftPadding - barWidth / 2;
        const barData = data.datasets[0].data[idx];
        const yBottomAnchor = y.getPixelForValue(Math.min(barData[0], barData[1])) + 20;
        const yTopAnchor = y.getPixelForValue(Math.max(barData[0], barData[1])) - 10;

        drawTopLabels(ctx, label, xAnchor, yTopAnchor, barWidth, boldFont, font);
        if (label.bottomLabel) {
          drawBottomLabels(ctx, label, xAnchor, yBottomAnchor, barWidth, boldFont, font);
        }
      });
    },
  };
}

export function lineToNextBarPlugin(): Plugin {
  return {
    id: 'lineToNextBar',
    afterDatasetDraw(chart) {
      const {
        ctx,
        data,
        scales: { x, y },
      } = chart;

      const segmentWidth = x.getPixelForValue(1) - x.getPixelForValue(0);
      const barPercentage =
        'barPercentage' in chart.config.data.datasets[0] ? chart.config.data.datasets[0].barPercentage : 1;
      const barWidth = segmentWidth * barPercentage;

      const backgroundColor = chart.config.data.datasets[0].backgroundColor;
      data.datasets[0].data.forEach((item, idx) => {
        if (!item || idx === data.datasets[0].data.length - 1) {
          return;
        }

        const nextItem = data.datasets[0].data[idx + 1];
        if (!nextItem) {
          return;
        }

        const startX = x.getPixelForValue(idx) + barWidth / 2;
        const startY = y.getPixelForValue(item[1]);

        const endX = x.getPixelForValue(idx + 1) - barWidth / 2;
        const endY = startY;

        ctx.beginPath();
        ctx.setLineDash([2, 3]);
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = backgroundColor[idx];
        ctx.stroke();
      });
    },
  };
}

export function summaryLabelsPlugin(data: DataSet, position: LabelPosition = LabelPosition.TOP): Plugin {
  return {
    id: 'summaryLabels',
    afterDatasetDraw(chart) {
      const {
        ctx,
        scales: { x, y },
      } = chart;
      const { values, changes } = data;
      // ctx.textAlign = 'left';
      ctx.font = `${DEFAULT_FONT_SIZE} ${DEFAULT_FONT}`;

      ctx.fillStyle = ChartColors.text;
      ctx.textAlign = 'center';
      values.formatted.forEach((value, idx) => {
        if (value !== null && value !== 'null') {
          ctx.fillText(value, x.getPixelForValue(idx), position === LabelPosition.TOP ? 16 : y.getBasePixel() + 85);
        }
      });
      if (changes) {
        changes.formatted.forEach((value, idx) => {
          if (value !== null && value !== 'null') {
            const color = changes.textColor[idx];
            ctx.font = `${DEFAULT_FONT_SIZE} ${DEFAULT_FONT}`;
            ctx.fillStyle = color;
            ctx.fillText(value, x.getPixelForValue(idx), position === LabelPosition.TOP ? 35 : y.getBasePixel() + 104);
          }
        });
      }
    },
  };
}

export function lineThroughPointsPlugin(): Plugin {
  return {
    id: 'lineThroughPointsPlugin',
    afterDraw(chart) {
      const {
        ctx,
        chartArea: { top, bottom },
      } = chart;

      if (chart.tooltip && chart.tooltip.getActiveElements().length) {
        const activePoint = chart.tooltip.getActiveElements()[0];
        const xCord = activePoint.element.x;

        // Draw a line
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(xCord, top);
        ctx.lineTo(xCord, bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'grey';
        ctx.setLineDash([2, 2]);
        ctx.stroke();
        ctx.restore();
      }
    },
  };
}
